// hooks/useAudioRecording.js
import { useState, useRef, useCallback, useEffect } from "react";

const useAudioRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  // Initialize audio recording
  const initializeRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus", // Good compression and quality
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        processAudioData();
      };

      return true;
    } catch (error) {
      console.error("Error initializing audio recording:", error);
      return false;
    }
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) {
      const initialized = await initializeRecording();
      if (!initialized) return false;
    }

    if (mediaRecorderRef.current.state === "inactive") {
      audioChunksRef.current = [];
      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
      return true;
    }
    return false;
  }, [initializeRecording]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      return true;
    }
    return false;
  }, []);

  // Process audio data and send to API
  const processAudioData = useCallback(async () => {
    if (audioChunksRef.current.length === 0) return;

    setIsProcessing(true);

    try {
      // Create blob from recorded chunks
      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm;codecs=opus",
      });

      // Convert to different format if needed (optional)
      const audioFile = new File([audioBlob], `recording_${Date.now()}.webm`, {
        type: "audio/webm;codecs=opus",
      });

      // Send to API
      await sendAudioToAPI(audioFile);
    } catch (error) {
      console.error("Error processing audio:", error);
    } finally {
      setIsProcessing(false);
      audioChunksRef.current = [];
    }
  }, []);

  // Send audio file to API
  const sendAudioToAPI = useCallback(async (audioFile) => {
    try {
      const formData = new FormData();
      formData.append("audio", audioFile);
      formData.append("timestamp", new Date().toISOString());
      formData.append("duration", audioFile.size); // You might want to calculate actual duration

      const response = await fetch("/api/upload-audio", {
        method: "POST",
        body: formData,
        headers: {
          // Don't set Content-Type, let browser set it with boundary for FormData
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();


      // You can return the result or trigger a callback here
      return result;
    } catch (error) {
      console.error("Error sending audio to API:", error);
      throw error;
    }
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    audioChunksRef.current = [];
    setIsRecording(false);
    setIsProcessing(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    isRecording,
    isProcessing,
    startRecording,
    stopRecording,
    cleanup,
  };
};

export default useAudioRecording;
